import { Link } from 'react-router-dom';
import { Sun, Moon, Calendar, ShieldCheck, Lock, Zap } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

export const loginThemes = {
  dark: {
    bg: '#0A0A0A',
    text: '#F5F5F5',
    text2: '#A3A3A3',
    text3: '#737373',
    text4: '#525252',
    border: 'rgba(255,255,255,0.06)',
    border2: 'rgba(255,255,255,0.1)',
    inputBg: 'rgba(255,255,255,0.04)',
    inputBorder: 'rgba(255,255,255,0.08)',
    inputFocus: 'rgba(255,255,255,0.2)',
    inputFocusBg: 'rgba(255,255,255,0.06)',
    inputFocusShadow: 'rgba(255,255,255,0.05)',
    btnSolidBg: '#F5F5F5',
    btnSolidColor: '#0A0A0A',
    btnSolidHover: '#E5E5E5',
    btnPrimaryBg: '#171717',
    btnPrimaryColor: '#F5F5F5',
    btnPrimaryBorder: 'rgba(255,255,255,0.1)',
    btnPrimaryHover: 'rgba(255,255,255,0.2)',
    btnPrimaryShadow: '0 1px 2px rgba(0,0,0,0.03)',
    dotPattern: 'rgba(255,255,255,0.03)',
    glow: 'rgba(255,255,255,0.012)',
    accent: '#F5F5F5',
    error: '#f87171',
    gradientText: 'linear-gradient(135deg, #F5F5F5 0%, #A3A3A3 100%)',
    roleActiveUnderline: '#F5F5F5',
  },
  light: {
    bg: '#FFFFFF',
    text: '#171717',
    text2: '#525252',
    text3: '#737373',
    text4: '#A3A3A3',
    border: 'rgba(0,0,0,0.04)',
    border2: 'rgba(0,0,0,0.06)',
    inputBg: 'rgba(0,0,0,0.02)',
    inputBorder: 'rgba(0,0,0,0.06)',
    inputFocus: 'rgba(0,0,0,0.15)',
    inputFocusBg: '#FFFFFF',
    inputFocusShadow: 'rgba(0,0,0,0.04)',
    btnSolidBg: '#171717',
    btnSolidColor: '#FFFFFF',
    btnSolidHover: '#262626',
    btnPrimaryBg: '#FFFFFF',
    btnPrimaryColor: '#171717',
    btnPrimaryBorder: 'rgba(0,0,0,0.08)',
    btnPrimaryHover: 'rgba(0,0,0,0.15)',
    btnPrimaryShadow: '0 1px 2px rgba(0,0,0,0.03)',
    dotPattern: 'rgba(0,0,0,0.03)',
    glow: 'rgba(0,0,0,0.012)',
    accent: '#171717',
    error: '#dc2626',
    gradientText: 'linear-gradient(135deg, #171717 0%, #404040 100%)',
    roleActiveUnderline: '#171717',
  },
};

export default function LoginLayout({ children, activeTab = 'user', showTabs = true }) {
  const { theme, toggle } = useTheme();
  const t = loginThemes[theme] || loginThemes.dark;
  const isDark = theme === 'dark';

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative" style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif", background: t.bg, color: t.text }}>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease-out forwards; }
      `}</style>

      {/* Background pattern */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `radial-gradient(${t.dotPattern} 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
      }} />
      <div className="fixed inset-0 pointer-events-none" style={{
        background: `radial-gradient(ellipse 60% 50% at 50% 45%, ${t.glow}, transparent)`,
      }} />


      {/* Top-right theme toggle */}
      <div className="fixed top-6 right-8 z-10">
        <button
          onClick={toggle}
          className="w-9 h-9 rounded-lg flex items-center justify-center transition hover:scale-105 relative"
          style={{ background: 'rgba(0,0,0,0.03)' }}
          title="Toggle theme"
        >
          {isDark ? (
            <Moon className="w-[18px] h-[18px]" style={{ color: t.text2 }} />
          ) : (
            <Sun className="w-[18px] h-[18px]" style={{ color: t.text2 }} />
          )}
        </button>
      </div>

      {/* Centered login form (no glass card) */}
      <div className="w-full max-w-[400px] relative z-10 fade-up">
        {/* Logo (centered) */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.04)' }}>
            <Calendar className="w-5 h-5" style={{ color: t.text }} />
          </div>
          <span className="text-xl font-extrabold tracking-tight" style={{ color: t.text }}>
            Schedule<span style={{ color: t.text4 }}>X</span>
          </span>
        </div>

        {showTabs && (
          <>
            {/* Role tabs */}
            <div className="flex items-center gap-8 border-b mb-8" style={{ borderColor: t.border2 }}>
              <Link
                to="/admin/login"
                className="pb-3 text-sm font-bold transition flex-1 text-center relative"
                style={{
                  color: activeTab === 'admin' ? t.text : t.text4,
                }}
              >
                Admin
                {activeTab === 'admin' && (
                  <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] rounded-[1px]" style={{ background: t.roleActiveUnderline }} />
                )}
              </Link>
              <Link
                to="/login"
                className="pb-3 text-sm font-bold transition flex-1 text-center relative"
                style={{
                  color: activeTab === 'user' ? t.text : t.text4,
                }}
              >
                Client Portal
                {activeTab === 'user' && (
                  <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] rounded-[1px]" style={{ background: t.roleActiveUnderline }} />
                )}
              </Link>
            </div>
          </>
        )}

        {/* Content */}
        {children}

        {/* Trust badges (outside card) */}
        <div className="flex items-center justify-center gap-5 mt-8 pt-5" style={{ borderTop: `1px solid ${t.border}` }}>
          <span className="flex items-center gap-1.5 text-[10px]" style={{ color: t.text4 }}>
            <Lock className="w-3 h-3" /> SOC 2
          </span>
          <span className="flex items-center gap-1.5 text-[10px]" style={{ color: t.text4 }}>
            <ShieldCheck className="w-3 h-3" /> GDPR
          </span>
          <span className="flex items-center gap-1.5 text-[10px]" style={{ color: t.text4 }}>
            <Zap className="w-3 h-3" /> 99.99% uptime
          </span>
        </div>
      </div>
    </div>
  );
}

/* Shared form styles hook */
export function useLoginStyles() {
  const { theme } = useTheme();
  const t = loginThemes[theme] || loginThemes.dark;
  return {
    t,
    input: {
      width: '100%',
      padding: '13px 16px',
      borderRadius: '12px',
      border: `1px solid ${t.inputBorder}`,
      background: t.inputBg,
      color: t.text,
      fontSize: '14px',
      fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
      transition: 'all 0.25s ease',
      outline: 'none',
    },
    inputFocus: {
      borderColor: t.inputFocus,
      background: t.inputFocusBg,
      boxShadow: `0 0 0 3px ${t.inputFocusShadow}`,
    },
    label: {
      display: 'block',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: t.text3,
      marginBottom: '8px',
    },
    btnSolid: {
      background: t.btnSolidBg,
      color: t.btnSolidColor,
      fontWeight: 700,
      border: 'none',
      cursor: 'pointer',
      fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
      transition: 'all 0.25s ease',
    },
    btnPrimary: {
      background: t.btnPrimaryBg,
      color: t.btnPrimaryColor,
      border: `1px solid ${t.btnPrimaryBorder}`,
      boxShadow: t.btnPrimaryShadow,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
      transition: 'all 0.25s ease',
    },
    divider: {
      height: '1px',
      background: t.border2,
      flex: 1,
    },
    dividerText: {
      fontSize: '11px',
      fontWeight: 500,
      color: t.text4,
    },
  };
}
